import { Icon } from "@pepperstone/pepper-ui-kit";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebouncedCallback } from "use-debounce";
import type {
  CurrencyPrimitive,
  FeeSettingsResponse,
  UnlimitableLimitResponse,
} from "@/api/cashier";
import { useFormatAmount } from "@/hooks/useFormatAmount";
import style from "./Exchanger.module.css";
import { ExchangerInput } from "./exchanger-input/ExchangerInput";
import { getPlaceholder } from "./helpers";

type ExchangerProps = {
  processingCurrencies: CurrencyPrimitive[];
  accountCurrency: CurrencyPrimitive;
  externalSelectedCurrency?: CurrencyPrimitive;
  externalTradingAmount?: number;
  externalProcessingAmount?: number;
  showExchange: boolean;
  selectedPaymentMethodLimits?: {
    min: UnlimitableLimitResponse;
    max: UnlimitableLimitResponse;
  };
  fee?: FeeSettingsResponse;
  onProcessingAmountChangeStart: (amount: number) => Promise<{
    isSuccess: boolean;
    newTradingAmount: number;
    newProcessingAmount: number;
  }>;
  onTradingAmountChangeStart: (amount: number) => Promise<{
    isSuccess: boolean;
    newTradingAmount: number;
    newProcessingAmount: number;
  }>;
  onCurrencyChangeStart?: (newCurrency: CurrencyPrimitive) => Promise<{
    isSuccess: boolean;
    newProcessingAmount: number;
    newTradingAmount: number;
  }>;
  onAmountInputChange?: () => void;
  errorMessage?: string;
  disabled?: boolean;
};

const DEBOUNCE_AMOUNT_MS = 300;

export const Exchanger = ({
  processingCurrencies,
  accountCurrency,
  disabled,
  externalSelectedCurrency,
  externalTradingAmount,
  externalProcessingAmount,
  onProcessingAmountChangeStart,
  onTradingAmountChangeStart,
  onCurrencyChangeStart,
  onAmountInputChange,
  selectedPaymentMethodLimits,
  fee,
  showExchange,
  errorMessage,
}: ExchangerProps) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const [tradingAmountIsDisabled, setTradingAmountIsDisabled] = useState(false);
  const [selectedProcessingCurrency, setSelectedProcessingCurrency] =
    useState<CurrencyPrimitive>(
      externalSelectedCurrency ?? processingCurrencies[0]
    );

  const [processingAmount, setProcessingAmount] = useState<string>("");
  const [tradingAmount, setTradingAmount] = useState<string>("");

  const { formatFee } = useFormatAmount();

  useEffect(() => {
    setProcessingAmount(
      typeof externalProcessingAmount === "undefined"
        ? ""
        : String(externalProcessingAmount)
    );
  }, [externalProcessingAmount]);
  useEffect(() => {
    setTradingAmount(
      typeof externalTradingAmount === "undefined"
        ? ""
        : String(externalTradingAmount)
    );
  }, [externalTradingAmount]);

  useEffect(() => {
    if (externalSelectedCurrency) {
      setSelectedProcessingCurrency(externalSelectedCurrency);
    }
  }, [externalSelectedCurrency]);

  // Debounced Handlers
  const debouncedTradingAmountChange = useDebouncedCallback(
    async (amount: number) => {
      await onTradingAmountChangeStart(amount);
    },
    DEBOUNCE_AMOUNT_MS
  );

  const debouncedProcessingAmountChange = useDebouncedCallback(
    async (amount: number) => {
      await onProcessingAmountChangeStart(amount);
    },
    DEBOUNCE_AMOUNT_MS
  );

  const debouncedCurrencyChange = useDebouncedCallback(
    async (newCurrency: CurrencyPrimitive) => {
      const res = await onCurrencyChangeStart?.(newCurrency);
      if (res?.isSuccess) {
        setTradingAmountIsDisabled(false);
      }
    },
    50
  );

  const cancelAllDebounced = useCallback(() => {
    debouncedTradingAmountChange.cancel();
    debouncedCurrencyChange.cancel();
    debouncedProcessingAmountChange.cancel();
  }, [
    debouncedTradingAmountChange,
    debouncedCurrencyChange,
    debouncedProcessingAmountChange,
  ]);

  // Input change handlers
  const handleTradingAmountChange = (amount: string) => {
    setTradingAmount(amount);
    onAmountInputChange?.();
    cancelAllDebounced();
    debouncedTradingAmountChange(Number(amount));
  };

  const handleProcessingAmountChange = (amount: string) => {
    setProcessingAmount(amount);
    onAmountInputChange?.();
    cancelAllDebounced();
    debouncedProcessingAmountChange(Number(amount));
  };

  const handleSelectedCurrencyChange = (currency: CurrencyPrimitive) => {
    setSelectedProcessingCurrency(currency);
    setTradingAmountIsDisabled(true);
    debouncedTradingAmountChange.cancel();
    debouncedCurrencyChange(currency);
  };

  const processingLabel = showExchange
    ? t("deposit.exchanger.you_pay_estimated")
    : t("deposit.exchanger.you_receive");

  const feeText = useMemo(
    () =>
      fee
        ? t("deposit.exchanger.fee_included", {
            fee: formatFee(fee, selectedProcessingCurrency),
          })
        : "",
    [fee, t, formatFee, selectedProcessingCurrency]
  );

  return (
    <div>
      <div className={style["amount-box"]}>
        <ExchangerInput
          label={processingLabel}
          testId="amount-box-processing-amount"
          value={processingAmount}
          onChange={handleProcessingAmountChange}
          processingCurrencies={processingCurrencies}
          selectedCurrency={selectedProcessingCurrency}
          setSelectedCurrency={handleSelectedCurrencyChange}
          disabled={disabled}
          placeholder={
            showExchange
              ? "--"
              : getPlaceholder(
                  locale,
                  accountCurrency.decimals ?? 2,
                  selectedPaymentMethodLimits
                )
          }
          subText={feeText}
          hasError={
            !showExchange && processingAmount !== "" && Boolean(errorMessage)
          }
          errorMessage={
            !showExchange && processingAmount !== "" ? errorMessage : undefined
          }
          locale={locale}
        />

        {showExchange && (
          <>
            <div className={style["arrow-wrapper"]}>
              <div className={style["arrow-container"]}>
                <div className={style.arrow} />
                <Icon icon="arrow-down-narrow" size="small" />
              </div>
            </div>

            <ExchangerInput
              label={t("deposit.exchanger.you_receive")}
              testId="amount-box-trading-amount"
              value={tradingAmount}
              onChange={handleTradingAmountChange}
              selectedCurrency={accountCurrency}
              disabled={
                tradingAmountIsDisabled || !externalSelectedCurrency || disabled
              }
              placeholder={getPlaceholder(
                locale,
                accountCurrency.decimals ?? 2,
                selectedPaymentMethodLimits
              )}
              hasError={tradingAmount !== "" && Boolean(errorMessage)}
              errorMessage={tradingAmount !== "" ? errorMessage : undefined}
              locale={locale}
            />
          </>
        )}
      </div>
    </div>
  );
};
